import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SlipPromotion = () => {
    // --- State ---
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // NEW: Track whether we are adding a new slip or changing an existing one
    const [uploadMode, setUploadMode] = useState('ADD');

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Slip Promotion",
            subtitle: "View and share promotional slips",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Slips...",
            noData: "No promotional slips found.",
            btnChange: "Change Image",
            btnDelete: "Delete Image",
            btnAdd: "Add Slip"
        },
        hi: {
            pageTitle: "पर्ची प्रचार",
            subtitle: "प्रचार पर्ची देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "पर्चियां लोड हो रही हैं...",
            noData: "कोई प्रचार पर्ची नहीं मिली।",
            btnChange: "छवि बदलें",
            btnDelete: "छवि हटाएं",
            btnAdd: "पर्ची जोड़ें"
        },
        gu: {
            pageTitle: "સ્લિપ પ્રમોશન",
            subtitle: "પ્રમોશનલ સ્લિપ્સ જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "સ્લિપ્સ લોડ થઈ રહી છે...",
            noData: "કોઈ પ્રમોશનલ સ્લિપ મળી નથી.",
            btnChange: "છબી બદલો",
            btnDelete: "છબી કાઢી નાખો",
            btnAdd: "સ્લિપ ઉમેરો"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchSlips = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "sleep"
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setSlips(response.data.data.result);
            } else {
                setSlips([]);
            }
        } catch (error) {
            console.error("Error fetching slip promotions:", error);
            setSlips([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSlips();
    }, [appId]);

    // --- Global Page Actions ---
    const handleAddImage = () => {
        setUploadMode('ADD');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleChangeImage = () => {
        setUploadMode('CHANGE');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // NEW: Handle File Upload for both Add and Change actions
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            // Strip the prefix to send raw base64
            const base64String = reader.result.split(',')[1];

            try {
                setLoading(true);
                const payload = {
                    app_id: appId?.toString(),
                    prachar_type: "sleep",
                    base64: base64String,
                    content: file.name,
                    create_by: userId?.toString() || "1"
                };

                // If changing an existing image, append its ID so the backend updates it
                if (uploadMode === 'CHANGE' && slips.length > 0) {
                    payload.id = slips[0].id.toString();
                }

                console.log(`Sending ${uploadMode} Payload:`, { ...payload, base64: "HIDDEN_BASE64" });

                const response = await axios.post(
                    `${BASE_URL}/api/ins_prachar_master`,
                    payload
                );

                if (response.data.success) {
                    await fetchSlips();
                } else {
                    alert("Failed to save the image. Server responded but success was false.");
                }
            } catch (error) {
                console.error("Error saving image:", error.response ? error.response.data : error.message);
                alert(`An error occurred while saving the image: ${error.message}`);
            } finally {
                setLoading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        };
    };

    // UPDATED: Delete API Call
    const handleDeleteImage = async () => {
        if (slips.length === 0) return;

        if (window.confirm("Are you sure you want to delete this slip photo?")) {
            try {
                // Taking the ID of the photo that was fetched
                const photoId = slips[0].id; 
                
                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    {
                        app_id: appId,
                        id: photoId?.toString(),
                        delete_by: userId?.toString() || "1"
                    }
                );

                if (response.data.success) {
                    // Re-fetch the slips to update the UI
                    fetchSlips();
                } else {
                    alert("Failed to delete the image.");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                alert("An error occurred while deleting the image.");
            }
        }
    };

    // --- Loading State ---
    if (loading && slips.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #10b981; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Buttons) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    {/* Hidden file input for uploading images */}
                    <input 
                        type="file" 
                        accept="image/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />

                    {/* CONDITIONAL RENDERING FOR BUTTONS - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        slips.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleAddImage}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleChangeImage}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {txt.btnChange}
                                </button>

                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={handleDeleteImage}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {slips.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🧾</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- SLIP GRID --- */}
            <div className="slip-grid pb-5">
                {slips.map((slip) => (
                    <div className="slip-card" key={slip.id}>
                        {/* Image Container */}
                        <div className="image-wrapper">
                            <img 
                                src={slip.content} 
                                alt={slip.name} 
                                className="promo-image"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = "https://via.placeholder.com/400x300?text=Slip+Not+Found";
                                }}
                            />
                        </div>

                        {/* Details Container (No Buttons Here) */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{slip.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {slip.designation || "User"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global (Solid Emerald Theme) */
                .btn-add {
                    background: #10b981;
                    color: #ffffff;
                    border: 1px solid #059669;
                    box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2);
                }
                .btn-add:hover {
                    background: #059669;
                    border-color: #047857;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(16, 185, 129, 0.3);
                }
                .btn-add:active {
                    background: #047857;
                    transform: translateY(0);
                }

                /* Edit Button Global */
                .btn-edit {
                    background: #ffffff;
                    color: #334155;
                    border: 1px solid #cbd5e1;
                }
                .btn-edit:hover {
                    background: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }
                .btn-edit:active {
                    background: #f1f5f9;
                }

                /* Delete Button Global */
                .btn-delete {
                    background: #fef2f2;
                    color: #ef4444;
                    border: 1px solid #fecaca;
                }
                .btn-delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
                .btn-delete:active {
                    background: #fecaca;
                }

                /* --- SLIP GRID --- */
                .slip-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .slip-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                }

                .slip-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Image Styling */
                .image-wrapper {
                    width: 100%;
                    height: 220px;
                    background: #f8fafc;
                    overflow: hidden;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .promo-image {
                    width: 100%;
                    height: 100%;
                    object-fit: contain; 
                    transition: transform 0.5s ease;
                }

                .slip-card:hover .promo-image {
                    transform: scale(1.03);
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                    border-top: 1px solid #f1f5f9; 
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #10b981; 
                    background: #ecfdf5;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #a7f3d0;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .slip-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                    .image-wrapper {
                        height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default SlipPromotion;